import classNames from 'classnames'
import parse from 'html-react-parser'
import { useTranslation } from 'next-i18next/pages'
import { Heading, Img } from '@boltenergy-be/design-system'
import Hero from 'sections/hero/Hero'
import { Button, Card } from '@boltenergy-be/design-system'
import styles from './About.module.scss'
import { setProductForSpecificSimulation } from 'store/customer/slice'
import { routes } from 'utils/routes'
import { useStoreDispatch } from 'hooks/store'
import { useRouter } from 'next/router'
import { BackgroundColors } from 'types/layout'
import Link from 'components/link/Link'

const About = () => {
  // Redux
  const dispatch = useStoreDispatch()
  // i18n
  const { t } = useTranslation('about')
  // Router
  const router = useRouter()

  // Constants
  const translationRoute = routes(router.locale)
  const cards = [
    {
      color: 'dark',
      title: t('mission.cards.one.title'),
      description: t('mission.cards.one.description'),
      cta: t('mission.cards.one.cta'),
      link: translationRoute.producers
    },
    {
      color: 'warm',
      title: t('mission.cards.two.title'),
      description: t('mission.cards.two.description'),
      cta: t('mission.cards.two.cta'),
      link: translationRoute.pricing
    },
    {
      color: 'soft',
      title: t('mission.cards.three.title'),
      description: t('mission.cards.three.description'),
      cta: t('mission.cards.three.cta'),
      link: translationRoute.blog
    }
  ] as const

  return (
    <main>
      {/* Hero */}
      <Hero
        backgroundColor={BackgroundColors.BRAND}
        image={{
          alt: '',
          publicId: 'website/producers/opwekkers'
        }}
        className={styles.hero}
      >
        <Hero.Title value={t('mission.hero.title')} />
        <Hero.Paragraph value={parse(t('mission.hero.description'))} />
        <Hero.CtaGroup>
          <Button
            variant="layered"
            onClick={async () => {
              // Reset specific product
              dispatch(setProductForSpecificSimulation(null))
              await router.push(translationRoute.simulation)
            }}
          >
            {t('mission.hero.cta')}
          </Button>
        </Hero.CtaGroup>
      </Hero>

      {/* Mission */}
      <section className="section">
        <div className={classNames('container', styles['split-layout'])}>
          <Img
            fill
            priority
            publicId="website/mission/founders"
            sizes="(min-width: 920px) 50vw, 100vw"
            alt={t('mission.founders.alt')}
            className={styles['founders-image']}
          />
          <div>
            <Heading as="h2" variant="d3" className="mt-none mb-500">
              {t('mission.founders.title')}
            </Heading>
            <p className="mt-none mb-500">{parse(t('mission.founders.description'))}</p>
            <p className="text-subdued">{t('mission.founders.alt')}</p>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section>
        <ul className={styles['card-list']}>
          {cards.map(({ color, title, description, cta, link }, idx) => (
            <li key={idx} className={styles['card-list-item']}>
              <Card color={color} className={styles.card}>
                <Heading as="h3" variant="h4" serif>
                  {title}
                </Heading>
                {parse(description)}
                <Link representation="button" variant="layered" href={link}>
                  {cta}
                </Link>
              </Card>
            </li>
          ))}
        </ul>
      </section>

      {/* Team */}
      <section className="section is-layer">
        <div className={classNames('container', styles['split-layout'])}>
          <div>
            <Heading as="h2" className="mt-none mb-500">
              {t('mission.team.title')}
            </Heading>
            {parse(t('mission.team.description'))}
            <Link representation="button" className="mt-500" href={translationRoute.careers}>
              {t('mission.team.cta')}
            </Link>
          </div>
          <Img
            fill
            priority
            publicId="website/mission/career-thumbnail"
            sizes="(min-width: 920px) 50vw, 100vw"
            alt={t('mission.team.title')}
          />
        </div>
      </section>
    </main>
  )
}

export default About
